import { useState, useEffect, useRef } from "react";
import adminApi from "../api/adminAxios";

export default function ManageCategories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [newName, setNewName]       = useState("");
  const [newImage, setNewImage]     = useState(null);
  const [newPreview, setNewPreview] = useState(null);
  const [adding, setAdding]         = useState(false);
  const [editId, setEditId]         = useState(null);
  const [editName, setEditName]     = useState("");
  const [editImage, setEditImage]   = useState(undefined);
  const [editPreview, setEditPreview] = useState(null);
  const [deleteId, setDeleteId]     = useState(null);
  const [error, setError]           = useState("");
  const newFileRef  = useRef(null);
  const editFileRef = useRef(null);

  useEffect(() => { fetchCategories(); }, []);

  const fetchCategories = () => {
    setLoading(true);
    adminApi.get("admin/categories/")
      .then(r => setCategories(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const handleAdd = async () => {
    const name = newName.trim();
    if (!name) { setError("Category name cannot be empty."); return; }
    if (categories.some(c => c.name.toLowerCase() === name.toLowerCase())) {
      setError("A category with this name already exists."); return;
    }
    setAdding(true); setError("");
    try {
      const fd = new FormData();
      fd.append("name", name);
      if (newImage instanceof File) fd.append("image", newImage);
      const res = await adminApi.post("admin/categories/", fd);
      setCategories(prev => [...prev, res.data]);
      setNewName(""); setNewImage(null); setNewPreview(null);
      if (newFileRef.current) newFileRef.current.value = "";
    } catch (err) {
      setError(err.response?.data?.error || "Failed to add category.");
    } finally { setAdding(false); }
  };

  const handleEdit = async (id) => {
    const name = editName.trim();
    if (!name) return;
    try {
      const fd = new FormData();
      fd.append("name", name);
      if (editImage instanceof File) {
        fd.append("image", editImage);
      } else if (editImage === null) {
        fd.append("remove_image", "true");
      }
      const res = await adminApi.patch(`admin/categories/${id}/`, fd);
      setCategories(prev => prev.map(c => c.id === id ? res.data : c));
      setEditId(null); setEditImage(undefined); setEditPreview(null);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to update category.");
    }
  };

  const openEdit = (cat) => {
    setEditId(cat.id); setEditName(cat.name);
    setEditImage(undefined); setEditPreview(cat.image || null); setError("");
  };

  const cancelEdit = () => {
    setEditId(null); setEditImage(undefined); setEditPreview(null);
  };

  const handleDelete = async (id) => {
    try {
      await adminApi.delete(`admin/categories/${id}/`);
      setCategories(prev => prev.filter(c => c.id !== id));
    } catch (err) {
      setError(err.response?.data?.error || "Cannot delete this category.");
    } finally { setDeleteId(null); }
  };

  const EMOJI_MAP = {
    "Floral": "🌸", "Woody": "🪵", "Fresh": "🍃", "Spice": "🌶️",
    "Gifts": "🎁", "Festive candles": "🪔", "Pillar candles": "🕯️",
    "container candles": "🕯️",
  };

  return (
    <div style={{ padding: "32px 36px", fontFamily: "Georgia, serif" }}>

      <div style={{ marginBottom: "28px" }}>
        <h1 style={{ fontSize: "24px", fontWeight: "700", color: "#1a1a1a" }}>Manage Categories</h1>
        <p style={{ fontSize: "13px", color: "#aaa", marginTop: "4px" }}>
          Add, rename, upload images, or remove product categories.
        </p>
      </div>

      {/* ── Add new ── */}
      <div style={{ background: "#fff", borderRadius: "14px", padding: "22px 24px",
        border: "1px solid #f0ebe3", marginBottom: "20px" }}>
        <p style={{ fontFamily: "Georgia, serif", fontSize: "12px", fontWeight: "700",
          color: "#5a3a1a", marginBottom: "14px", textTransform: "uppercase",
          letterSpacing: "0.08em" }}>
          Add New Category
        </p>
        <div style={{ display: "flex", gap: "14px", alignItems: "flex-start", flexWrap: "wrap" }}>

          {/* Image zone */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "5px" }}>
            <div onClick={() => newFileRef.current?.click()}
              style={{ width: "72px", height: "72px", borderRadius: "10px",
                border: `2px dashed ${newPreview ? "#e07b39" : "#e8d5b8"}`,
                background: "#fffaf4", cursor: "pointer", overflow: "hidden",
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0, transition: "border-color 0.2s" }}
              onMouseEnter={e => e.currentTarget.style.borderColor = "#e07b39"}
              onMouseLeave={e => e.currentTarget.style.borderColor = newPreview ? "#e07b39" : "#e8d5b8"}>
              {newPreview
                ? <img src={newPreview} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                : <span style={{ fontSize: "22px" }}>📷</span>
              }
            </div>
            <span style={{ fontFamily: "Georgia, serif", fontSize: "9px", color: "#bbb" }}>
              Click to upload
            </span>
            <input ref={newFileRef} type="file" accept="image/*" style={{ display: "none" }}
              onChange={e => {
                const f = e.target.files[0];
                if (f) { setNewImage(f); setNewPreview(URL.createObjectURL(f)); }
              }} />
          </div>

          <div style={{ flex: 1, minWidth: "200px" }}>
            <input value={newName}
              onChange={e => { setNewName(e.target.value); setError(""); }}
              onKeyDown={e => e.key === "Enter" && handleAdd()}
              placeholder="e.g. Citrus, Herbal, Seasonal..."
              style={{ width: "100%", padding: "10px 14px", borderRadius: "9px",
                border: `1.5px solid ${error ? "#e57373" : "#e8d5b8"}`,
                background: "#fffaf4", fontFamily: "Georgia, serif", fontSize: "13px",
                outline: "none", boxSizing: "border-box" }} />
            {error && <p style={{ fontFamily: "Georgia, serif", fontSize: "11px",
              color: "#c0392b", marginTop: "5px" }}>⚠ {error}</p>}
            <p style={{ fontFamily: "Georgia, serif", fontSize: "10px",
              color: "#bbb", marginTop: "5px" }}>
              Photo is optional — it appears as the card background on the home page.
            </p>
          </div>

          <button onClick={handleAdd} disabled={adding}
            style={{ padding: "12px 22px", borderRadius: "10px",
              background: adding ? "#e0a870" : "linear-gradient(135deg,#e07b39,#c96a18)",
              color: "#fff", border: "none", cursor: adding ? "not-allowed" : "pointer",
              fontFamily: "Georgia, serif", fontSize: "13px", fontWeight: "700",
              whiteSpace: "nowrap", boxShadow: "0 4px 14px rgba(201,106,24,.25)",
              alignSelf: "flex-start" }}>
            {adding ? "Adding..." : "+ Add Category"}
          </button>
        </div>
      </div>

      {/* ── Table ── */}
      <div style={{ background: "#fff", borderRadius: "14px",
        border: "1px solid #f0ebe3", overflow: "hidden" }}>
        <div style={{ display: "grid", gridTemplateColumns: "60px 80px 1fr 130px 110px",
          gap: "8px", padding: "12px 24px", background: "#f7f2eb" }}>
          {["ID","IMAGE","CATEGORY NAME","PRODUCTS","ACTIONS"].map(h => (
            <span key={h} style={{ fontFamily: "Georgia, serif", fontSize: "9px",
              fontWeight: "700", color: "#aaa", letterSpacing: "0.08em" }}>{h}</span>
          ))}
        </div>

        {loading ? (
          <p style={{ padding: "32px", color: "#aaa", fontFamily: "Georgia, serif",
            fontSize: "13px" }}>Loading categories...</p>
        ) : categories.length === 0 ? (
          <p style={{ padding: "32px", color: "#aaa", fontFamily: "Georgia, serif",
            fontSize: "13px", textAlign: "center" }}>No categories yet.</p>
        ) : (
          categories.map(cat => (
            <div key={cat.id} style={{ display: "grid",
              gridTemplateColumns: "60px 80px 1fr 130px 110px",
              gap: "8px", padding: "14px 24px",
              borderBottom: "1px solid #f7f2eb", alignItems: "center" }}>

              <span style={{ fontFamily: "Georgia, serif", fontSize: "11px",
                fontWeight: "700", color: "#e07b39" }}>#{cat.id}</span>

              {/* Image cell */}
              {editId === cat.id ? (
                <div style={{ display: "flex", flexDirection: "column",
                  alignItems: "center", gap: "4px" }}>
                  <div onClick={() => editFileRef.current?.click()}
                    style={{ width: "56px", height: "56px", borderRadius: "8px",
                      border: "2px dashed #e07b39", background: "#fffaf4",
                      cursor: "pointer", overflow: "hidden",
                      display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {editPreview
                      ? <img src={editPreview} alt=""
                          style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      : <span style={{ fontSize: "18px" }}>📷</span>
                    }
                  </div>
                  <input ref={editFileRef} type="file" accept="image/*"
                    style={{ display: "none" }}
                    onChange={e => {
                      const f = e.target.files[0];
                      if (f) { setEditImage(f); setEditPreview(URL.createObjectURL(f)); }
                    }} />
                  {editPreview && (
                    <button onClick={() => { setEditImage(null); setEditPreview(null); }}
                      style={{ background: "none", border: "none", cursor: "pointer",
                        fontFamily: "Georgia, serif", fontSize: "9px",
                        color: "#c0392b", padding: 0 }}>✕ remove</button>
                  )}
                </div>
              ) : (
                <div style={{ width: "56px", height: "56px", borderRadius: "8px",
                  overflow: "hidden", background: "#f0ebe3",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: "22px", border: cat.image ? "2px solid #e8d5b8" : "none" }}>
                  {cat.image
                    ? <img src={cat.image} alt={cat.name}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        onError={e => { e.target.style.display = "none"; }} />
                    : (EMOJI_MAP[cat.name] || "🕯️")
                  }
                </div>
              )}

              {/* Name */}
              {editId === cat.id ? (
                <div>
                  <input value={editName} autoFocus
                    onChange={e => setEditName(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") handleEdit(cat.id); if (e.key === "Escape") cancelEdit(); }}
                    style={{ width: "100%", padding: "7px 10px", borderRadius: "7px",
                      border: "1.5px solid #e07b39", background: "#fffaf4",
                      fontFamily: "Georgia, serif", fontSize: "13px",
                      outline: "none", boxSizing: "border-box" }} />
                </div>
              ) : (
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <span style={{ fontFamily: "Georgia, serif", fontSize: "14px",
                    fontWeight: "600", color: "#1a1a1a" }}>{cat.name}</span>
                  {cat.image && (
                    <span style={{ fontSize: "9px", color: "#3a8a68",
                      background: "#eefaf4", borderRadius: "999px",
                      padding: "2px 8px", fontFamily: "Georgia, serif",
                      fontWeight: "600" }}>✓ Image</span>
                  )}
                </div>
              )}

              <span style={{ padding: "3px 12px", borderRadius: "999px",
                background: "#f0ebe3", fontFamily: "Georgia, serif", fontSize: "11px",
                fontWeight: "600", color: "#7a5c3a", display: "inline-block",
                width: "fit-content" }}>
                {cat.product_count ?? "—"} products
              </span>

              {editId === cat.id ? (
                <div style={{ display: "flex", gap: "6px" }}>
                  <button onClick={() => handleEdit(cat.id)}
                    style={{ padding: "5px 14px", borderRadius: "7px",
                      background: "#3a8a68", color: "#fff", border: "none",
                      cursor: "pointer", fontFamily: "Georgia, serif",
                      fontSize: "11px", fontWeight: "700" }}>Save</button>
                  <button onClick={cancelEdit}
                    style={{ padding: "5px 10px", borderRadius: "7px",
                      background: "#f0ebe3", color: "#888", border: "none",
                      cursor: "pointer", fontFamily: "Georgia, serif", fontSize: "11px" }}>✕</button>
                </div>
              ) : (
                <div style={{ display: "flex", gap: "6px" }}>
                  <button onClick={() => openEdit(cat)} title="Edit"
                    style={{ background: "#fff4ec", border: "none", borderRadius: "6px",
                      padding: "5px 9px", cursor: "pointer", fontSize: "13px" }}>✏️</button>
                  <button onClick={() => { setDeleteId(cat.id); setError(""); }} title="Delete"
                    style={{ background: "#fff0f0", border: "none", borderRadius: "6px",
                      padding: "5px 9px", cursor: "pointer", fontSize: "13px" }}>🗑️</button>
                </div>
              )}
            </div>
          ))
        )}

        {!loading && (
          <div style={{ padding: "12px 24px", background: "#faf7f2" }}>
            <span style={{ fontFamily: "Georgia, serif", fontSize: "11px", color: "#aaa" }}>
              {categories.length} categor{categories.length === 1 ? "y" : "ies"} total
            </span>
          </div>
        )}
      </div>

      {/* Delete modal */}
      {deleteId && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)",
          zIndex: 1000, display: "flex", alignItems: "center",
          justifyContent: "center", padding: "24px" }}>
          <div style={{ background: "#fff", borderRadius: "18px", padding: "28px",
            maxWidth: "360px", width: "100%", textAlign: "center",
            boxShadow: "0 16px 60px rgba(0,0,0,.15)" }}>
            <div style={{ fontSize: "40px", marginBottom: "12px" }}>🗂️</div>
            <h3 style={{ fontFamily: "Georgia, serif", fontSize: "16px",
              fontWeight: "700", color: "#1a1a1a", marginBottom: "8px" }}>Delete Category?</h3>
            <p style={{ fontFamily: "Georgia, serif", fontSize: "13px",
              color: "#888", marginBottom: "24px" }}>
              ⚠ Products using this category may be affected.
            </p>
            {error && <p style={{ fontFamily: "Georgia, serif", fontSize: "12px",
              color: "#c0392b", marginBottom: "16px", background: "#fff0f0",
              padding: "8px 12px", borderRadius: "8px" }}>{error}</p>}
            <div style={{ display: "flex", gap: "10px" }}>
              <button onClick={() => { setDeleteId(null); setError(""); }}
                style={{ flex: 1, padding: "11px", borderRadius: "10px",
                  border: "1.5px solid #e8d5b8", background: "transparent",
                  color: "#888", cursor: "pointer",
                  fontFamily: "Georgia, serif", fontSize: "13px" }}>Keep It</button>
              <button onClick={() => handleDelete(deleteId)}
                style={{ flex: 1, padding: "11px", borderRadius: "10px",
                  background: "#c0392b", color: "#fff", border: "none",
                  cursor: "pointer", fontFamily: "Georgia, serif",
                  fontSize: "13px", fontWeight: "700" }}>Yes, Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}